// src/components/admin/compensation/EmployeeCompensationBlocks.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Search, Filter, Download, TrendingUp, Users, DollarSign,
    Grid, List, TableProperties, ChevronDown, SlidersHorizontal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEmployees } from '../../../contexts/EmployeeContext';
import EmployeeCard from './EmployeeCard';
import PayrollFlowSystem from '../PayrollFlowSystem';

const EmployeeCompensationBlocks = ({ onEmployeeSelect }) => {
    const navigate = useNavigate();
    const { employees, loading: employeesLoading, departments } = useEmployees();
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    // State for search and filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        department: 'all',
        location: 'all',
        status: 'active',
        salaryMin: 0,
        salaryMax: 10000000
    });
    const [sortBy, setSortBy] = useState('name');
    const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'table'
    const [showFilters, setShowFilters] = useState(false);

    // Get unique locations from employees
    const locations = useMemo(() => {
        const uniqueLocations = [...new Set(employees.map(emp => emp.work_location || emp.location).filter(Boolean))];
        return uniqueLocations.sort();
    }, [employees]);

    // Helper to get CTC from employee object (checks multiple possible field names)
    const getEmployeeCTC = (emp) => {
        return emp.annual_ctc ||
               emp.ctc ||
               emp.pay_package?.annual_ctc ||
               emp.pay_package?.ctc ||
               0;
    };

    // Filter and search employees
    const filteredEmployees = useMemo(() => {
        return employees.filter(emp => {
            // Only show active employees by default (unless filter changed)
            if (filters.status === 'active' && emp.employment_status !== 'active') return false;
            if (filters.status === 'inactive' && emp.employment_status === 'active') return false;
            if (filters.status === 'on-leave' && emp.employment_status !== 'on_leave') return false;

            // Search filter
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matches =
                    emp.name?.toLowerCase().includes(term) ||
                    emp.employee_code?.toLowerCase().includes(term) ||
                    emp.designation?.toLowerCase().includes(term) ||
                    emp.email?.toLowerCase().includes(term);
                if (!matches) return false;
            }

            // Department filter
            if (filters.department !== 'all') {
                const empDept = departments.find(d => d.id === emp.department_id);
                if (empDept?.name !== filters.department) return false;
            }

            // Location filter
            if (filters.location !== 'all') {
                if ((emp.work_location || emp.location) !== filters.location) return false;
            }

            // Salary range filter
            const salary = getEmployeeCTC(emp);
            if (salary < filters.salaryMin || salary > filters.salaryMax) return false;

            return true;
        });
    }, [employees, searchTerm, filters, departments]);

    // Sort employees
    const sortedEmployees = useMemo(() => {
        const sorted = [...filteredEmployees];
        switch (sortBy) {
            case 'name':
                return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            case 'ctc_high':
                return sorted.sort((a, b) => getEmployeeCTC(b) - getEmployeeCTC(a));
            case 'ctc_low':
                return sorted.sort((a, b) => getEmployeeCTC(a) - getEmployeeCTC(b));
            case 'designation':
                return sorted.sort((a, b) => (a.designation || '').localeCompare(b.designation || ''));
            case 'joining_date':
                return sorted.sort((a, b) => new Date(a.joining_date || 0) - new Date(b.joining_date || 0));
            default:
                return sorted;
        }
    }, [filteredEmployees, sortBy]);

    // Calculate statistics
    const statistics = useMemo(() => {
        const totalEmployees = filteredEmployees.length;
        const totalCTC = filteredEmployees.reduce((sum, emp) => sum + getEmployeeCTC(emp), 0);
        const avgCTC = totalEmployees > 0 ? totalCTC / totalEmployees : 0;

        return {
            totalEmployees,
            totalCTC,
            avgCTC
        };
    }, [filteredEmployees]);

    // Export to CSV
    const handleExportCSV = () => {
        const headers = ['Employee Code', 'Name', 'Designation', 'Department', 'Location', 'Annual CTC', 'Monthly CTC', 'Status'];
        const rows = sortedEmployees.map(emp => {
            const dept = departments.find(d => d.id === emp.department_id);
            const ctc = getEmployeeCTC(emp);
            return [
                emp.employee_code || '',
                emp.name || '',
                emp.designation || '',
                dept?.name || '',
                emp.work_location || emp.location || '',
                ctc,
                Math.round(ctc / 12),
                emp.employment_status || 'active'
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `employee_compensation_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const formatCurrency = (amount) => {
        if (amount >= 10000000) {
            return `₹${(amount / 10000000).toFixed(2)}Cr`;
        } else if (amount >= 100000) {
            return `₹${(amount / 100000).toFixed(2)}L`;
        } else {
            return `₹${amount.toLocaleString('en-IN')}`;
        }
    };

    // Handle view details click
    const handleViewDetails = (employeeId) => {
        if (onEmployeeSelect) {
            onEmployeeSelect(employeeId);
        } else {
            setSelectedEmployeeId(employeeId);
        }
    };

    // If an employee is selected, show their detailed compensation view
    if (selectedEmployeeId) {
        return (
            <PayrollFlowSystem
                preselectedEmployeeId={selectedEmployeeId}
                onBack={() => setSelectedEmployeeId(null)}
            />
        );
    }

    if (employeesLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-64 space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading employees...</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Employee Compensation</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Manage and view employee salary structures
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExportCSV}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
                    >
                        <Download className="w-4 h-4" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Statistics */}
            <div className="tour-comp-stats grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Employees</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {statistics.totalEmployees}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total CTC</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                                {formatCurrency(statistics.totalCTC)}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Average CTC</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                                {formatCurrency(statistics.avgCTC)}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, code, designation, or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded ${
                                viewMode === 'grid'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                    : 'text-gray-600 dark:text-gray-400'
                            }`}
                            title="Grid View"
                        >
                            <Grid className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded ${
                                viewMode === 'list'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                    : 'text-gray-600 dark:text-gray-400'
                            }`}
                            title="List View"
                        >
                            <List className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-2 rounded ${
                                viewMode === 'table'
                                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                    : 'text-gray-600 dark:text-gray-400'
                            }`}
                            title="Table View"
                        >
                            <TableProperties className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Filters Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                            showFilters
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                        Filters
                    </button>
                </div>

                {/* Advanced Filters */}
                {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {/* Department Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Department
                            </label>
                            <select
                                value={filters.department}
                                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept.id} value={dept.name}>{dept.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Location Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Location
                            </label>
                            <select
                                value={filters.location}
                                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="all">All Locations</option>
                                {locations.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Status
                            </label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="active">Active</option>
                                <option value="all">All Status</option>
                                <option value="inactive">Inactive</option>
                                <option value="on-leave">On Leave</option>
                            </select>
                        </div>

                        {/* Sort By */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Sort By
                            </label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="name">Name (A-Z)</option>
                                <option value="ctc_high">CTC (High to Low)</option>
                                <option value="ctc_low">CTC (Low to High)</option>
                                <option value="designation">Designation</option>
                                <option value="joining_date">Joining Date</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {sortedEmployees.length} of {employees.length} employees
                </p>
            </div>

            {/* Employee Grid/List/Table */}
            {sortedEmployees.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">No employees found</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Try adjusting your search or filters
                    </p>
                </div>
            ) : (
                <>
                    {viewMode === 'grid' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sortedEmployees.map(employee => (
                                <EmployeeCard
                                    key={employee.id}
                                    employee={employee}
                                    department={departments.find(d => d.id === employee.department_id)}
                                    onViewDetails={() => handleViewDetails(employee.id)}
                                />
                            ))}
                        </div>
                    )}

                    {viewMode === 'list' && (
                        <div className="space-y-4">
                            {sortedEmployees.map(employee => (
                                <EmployeeCard
                                    key={employee.id}
                                    employee={employee}
                                    department={departments.find(d => d.id === employee.department_id)}
                                    onViewDetails={() => handleViewDetails(employee.id)}
                                    viewMode="list"
                                />
                            ))}
                        </div>
                    )}

                    {viewMode === 'table' && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Employee
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Designation
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Department
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Annual CTC
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Monthly CTC
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {sortedEmployees.map(employee => {
                                            const dept = departments.find(d => d.id === employee.department_id);
                                            const ctc = getEmployeeCTC(employee);
                                            return (
                                                <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                                                                {employee.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'NA'}
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {employee.name}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {employee.employee_code}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {employee.designation || 'Not Specified'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {dept?.name || 'Not Specified'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">
                                                        {formatCurrency(ctc)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {formatCurrency(ctc / 12)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                        <button
                                                            onClick={() => handleViewDetails(employee.id)}
                                                            className="text-blue-600 dark:text-blue-400 hover:underline"
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EmployeeCompensationBlocks;
